import React, { useState, useEffect } from 'react';
import { NotebookPen } from 'lucide-react';
import * as api from '../../lib/api';

const MAX_LINE_LENGTH = 42;

const firstLine = (content) => {
  const line = (content || '').split('\n')[0].trim();
  if (!line) return '(empty post)';
  return line.length > MAX_LINE_LENGTH ? `${line.slice(0, MAX_LINE_LENGTH).trimEnd()}…` : line;
};

const shortTimeAgo = (dateStr) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * Auto-populated, read-only widget — no settings panel, since there's
 * nothing to configure. It just reflects the owner's own recent original
 * posts (no reposts, self-posts, or space posts), so it renders the same
 * in and out of edit mode.
 */
const RecentPostsWidget = ({ profileData, onNavigate }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profileData?.id) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.getRecentPosts(profileData.id, 5)
      .then((res) => { if (!cancelled) setPosts(res.data || []); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [profileData?.id]);

  return (
    <div
      className="relative w-[260px] rounded-2xl shadow-lg overflow-hidden"
      style={{ background: '#FFFDF3' }}
      data-testid="recent-posts-widget"
    >
      <div className="flex justify-center gap-3 pt-2.5 pb-1" aria-hidden="true">
        {Array.from({ length: 7 }).map((_, i) => (
          <span key={i} className="w-1.5 h-1.5 rounded-full bg-black/15" />
        ))}
      </div>
      <div className="absolute top-9 bottom-0 left-8 w-px bg-red-300/40" aria-hidden="true" />

      <div className="relative pl-9 pr-4 pb-4 pt-1">
        <p className="font-heading text-sm font-bold text-[#4A2E2E] mb-2 flex items-center gap-1.5">
          <NotebookPen className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
          My Recent Posts
        </p>

        {loading && <p className="text-xs text-[#8a7a6a] py-4 text-center">Loading…</p>}

        {!loading && posts.length === 0 && (
          <p className="text-xs text-[#8a7a6a] py-4 text-center">No posts yet.</p>
        )}

        {!loading && posts.length > 0 && (
          <ul>
            {posts.map((p, i) => (
              <li key={p.id} className={i > 0 ? 'border-t border-black/10' : ''}>
                <button
                  onClick={() => onNavigate?.(`post:${p.id}`)}
                  className="w-full text-left py-2 group"
                  data-testid={`recent-post-link-${p.id}`}
                >
                  <p className="text-xs text-[#4A2E2E] truncate group-hover:text-primary transition-colors">
                    {firstLine(p.content)}
                  </p>
                  <p className="text-[10px] text-[#8a7a6a] mt-0.5">{shortTimeAgo(p.created_at)}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RecentPostsWidget;
